import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, ListChecks, Layers, Sparkles, LogOut, Languages, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import ReadAloud from './ReadAloud';
import { OVERVIEW_STATS, LAYER_AVERAGES } from '../mock';
import Overview from './pages/Overview';
import UseCases from './pages/UseCases';
import Readiness from './pages/Readiness';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, toggle, isRTL } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { key: 'overview', icon: LayoutGrid, label: t('overview') },
    { key: 'useCases', icon: ListChecks, label: t('useCases') },
    { key: 'readiness', icon: Layers, label: t('readiness') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const readAloudContext = () => {
    if (tab === 'overview') {
      return { stats: OVERVIEW_STATS, layers: LAYER_AVERAGES.map((l) => ({ layer: l.en, score: l.score })) };
    }
    if (tab === 'readiness') {
      const sorted = [...LAYER_AVERAGES].sort((a, b) => a.score - b.score);
      return {
        layers: LAYER_AVERAGES.map((l) => ({ layer: l.en, score: l.score })),
        weakest: { layer: sorted[0].en, score: sorted[0].score },
        strongest: { layer: sorted[sorted.length - 1].en, score: sorted[sorted.length - 1].score },
      };
    }
    return {
      totalUseCases: OVERVIEW_STATS.totalUseCases,
      byStatus: { scaled: OVERVIEW_STATS.scaled, live: OVERVIEW_STATS.live, piloting: OVERVIEW_STATS.piloting, backlog: OVERVIEW_STATS.backlog },
      avgAbsorption: OVERVIEW_STATS.avgAbsorption,
      avgValue: OVERVIEW_STATS.avgValue,
    };
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-100">
        <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
          <Sparkles className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
        </div>
        <span className="font-display font-bold text-slate-900 text-sm leading-tight">{t('brand')}</span>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = tab === n.key;
          return (
            <button key={n.key}
              onClick={() => { setTab(n.key); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
              <Icon className="w-[18px] h-[18px]" />
              {n.label}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-100 space-y-1">
        <button onClick={toggle}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
          <Languages className="w-[18px] h-[18px]" />
          {t('langToggle')}
        </button>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors">
          <LogOut className="w-[18px] h-[18px]" />
          {t('logout')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col w-64 bg-white ${isRTL ? 'border-l' : 'border-r'} border-slate-200 fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'}`}>
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className={`absolute inset-y-0 ${isRTL ? 'right-0' : 'left-0'} w-64 bg-white shadow-xl`}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className={`flex-1 ${isRTL ? 'lg:mr-64' : 'lg:ml-64'} min-w-0`}>
        <header className="h-16 bg-white/80 backdrop-blur border-b border-slate-200 flex items-center justify-between px-5 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="font-display font-bold text-slate-900 text-lg">
              {nav.find((n) => n.key === tab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <ReadAloud screen={tab} context={readAloudContext()} />
            <div className={`text-${isRTL ? 'left' : 'right'} hidden sm:block`}>
              <div className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</div>
              <div className="text-xs text-slate-400 leading-tight">{user?.email}</div>
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-indigo-100" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase() || 'A'}
              </div>
            )}
          </div>
        </header>
        <main className="p-5 lg:p-8 max-w-[1400px]">
          {tab === 'overview' && <Overview />}
          {tab === 'useCases' && <UseCases />}
          {tab === 'readiness' && <Readiness />}
        </main>
      </div>
    </div>
  );
}
